class VIPPointsProductsList extends HTMLElement {
  constructor() {
    super();
    this.customer_has_login = this.dataset.customer === 'true';
    this.template = this.querySelector('.vip-points-products__item').cloneNode(true);
    this.grid = this.querySelector('.vip-points-products__grid');
    this.cart = document.querySelector('g-cart-drawer');
    this.loading = this.querySelector('[data-loading]');
    this.empty = this.querySelector('[data-empty]');
    this.container = this.querySelector('[data-container]');
    this.isSubmitting = false;
    this.productData = null;
    this.modal = this.querySelector('#redeemProductConfirmModal');
    this.confirmView = this.modal.querySelector('.redeem-modal__confirm');
    this.currentProduct = null;
  }

  connectedCallback() {
    this.setupEventListeners();
    this.fetchProducts();
  }

  setupEventListeners() {
    this.addEventListener('click', (e) => {
      const addButton = e.target.closest('.vip-points-products__item-button');
      if (addButton && !addButton.classList.contains('g-button--disabled')) {
        const productId = this.getSelectedProductId(addButton);
        const discountId = this.getSelectedDiscountId(addButton);
        if (productId && discountId) {
          this.handleRedeem(productId, discountId);
        }
      }
    });

    this.modal.addEventListener('click', (e) => {
      const action = e.target.closest('[data-action]')?.dataset.action;
      if (action) {
        switch (action) {
          case 'cancel':
            this.modal.close();
            break;
          case 'confirm':
            this.handleConfirmRedeem();
            break;
        }
      }
    });

    // 监听变体选择器变化
    this.addEventListener('change', (e) => {
      const select = e.target;
      if (select.matches('[data-product-select]')) {
        this.handleProductChange(select);
      } else if (select.matches('[data-color-select], [data-style-select]')) {
        this.handleVariantChange(select);
      }
    });
  }

  async fetchProducts() {
    try {
      this.showLoading();

      const response = await fetch('/apps/esrvip/common/exchanheable-products');
      const data = await response.json();

      if (data.code === 200) {
        const { list } = data.data;

        if (!list || list.length === 0) {
          this.showEmpty();
        } else {
          this.renderProducts(list);
          this.showContent();
        }
      } else {
        throw new Error(data.message);
      }
    } catch (error) {
      console.error('Failed to fetch products:', error);
      this.showEmpty();
    } finally {
      this.hideLoading();
    }
  }

  async handleRedeem(productId, discountId) {
    if (!this.customer_has_login || this.isSubmitting) {
      return;
    }

    const button = this.querySelector(`[data-product-id="${productId}"]`);
    if (!button) return;

    const productTitle = button
      .closest('.vip-points-products__item')
      .querySelector('.vip-points-products__item-name').textContent;

    this.currentProduct = { id: productId, discountId, title: productTitle };
    this.showConfirmView(productTitle);
    this.modal.open();
  }

  async handleConfirmRedeem() {
    if (!this.currentProduct || this.isSubmitting) return;

    try {
      this.isSubmitting = true;
      const confirmButton = this.modal.querySelector('[data-action="confirm"]');
      confirmButton.classList.add('g-button--loading');

      await this.exchangePoints(this.currentProduct.id, this.currentProduct.discountId);
      await this.addToCartAndUpdateUI(this.currentProduct.id);
      await this.fetchProducts();

      this.modal.close();
    } catch (error) {
      this.showError(error.message);
    } finally {
      this.isSubmitting = false;
      const confirmButton = this.modal.querySelector('[data-action="confirm"]');
      confirmButton.classList.remove('g-button--loading');
    }
  }

  showConfirmView(title) {
    this.modal.querySelector('.redeem-modal__product-title').textContent = title;
  }

  showError(message) {
    document.dispatchEvent(
      new CustomEvent('notification:show', {
        detail: {
          showOptions: {
            type: 'error',
            message,
            last: 3000,
          },
        },
      })
    );
  }

  // 检查是否可以加入购物车
  canAddToCart() {
    if (!this.customer_has_login || this.isSubmitting) {
      return false;
    }
    return true;
  }

  // 积分兑换
  async exchangePoints(productId, discountId) {
    const formData = this.createExchangeFormData(productId, discountId);
    const response = await this.fetchExchange(formData);

    if (response.code !== 200) {
      throw new Error(response.message || 'Exchange failed');
    }
    return response;
  }

  createExchangeFormData(productId, discountId) {
    const formData = new FormData();
    formData.append('type', 'exchange_product');
    formData.append('product_id', productId);
    formData.append('discount_id', discountId);
    return formData;
  }

  async fetchExchange(formData) {
    const response = await fetch('/apps/esrvip/vip/exchange-discount', {
      method: 'POST',
      body: formData,
    });
    return response.json();
  }

  // 加入购物车
  async addToCartAndUpdateUI(productId) {
    const body = this.createCartFormData(productId);
    const cartData = await this.fetchCartAdd(body);

    if (cartData?.items?.length > 0) {
      await this.updateCartUI(cartData);
      return cartData;
    } else {
      this.emitCartError(productId, cartData);
      throw new Error(cartData.message || 'Add to cart failed');
    }
  }

  createCartFormData(productId) {
    const currentProduct = this.findCurrentProduct(productId);
    const variantId = currentProduct?.variant_id;
    const body = {
      items: [
        {
          id: variantId,
          quantity: 1,
          properties: {},
        },
      ],
    };

    // 添加商品属性
    this.appendProductProperties(body, productId);

    // 添加购物车部分信息
    this.appendCartSections(body);

    return body;
  }

  appendProductProperties(body, productId) {
    const currentProduct = this.findCurrentProduct(productId);
    if (!currentProduct?.metafields) return;

    const properties = this.getProductProperties(currentProduct.metafields);
    Object.entries(properties).forEach(([key, value]) => {
      body.items[0].properties[key] = value;
    });
  }

  getProductProperties(metafields) {
    return metafields.reduce((props, meta) => {
      if (meta.namespace === 'compose' && meta.key !== 'product_list') {
        props[meta.key] = meta.value;
      }
      return props;
    }, {});
  }

  appendCartSections(body) {
    if (!this.cart) return;

    body.sections = this.cart.getSectionsToRender().map((section) => section.id);
    body.sections_url = window.location.pathname;
  }

  async fetchCartAdd(body) {
    const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
      method: 'POST',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });

    const data = await response.json();

    // 处理错误响应
    if (data.status === 422) {
      throw new Error(data.description || 'Add to cart failed');
    }

    return data;
  }

  emitCartError(productId, cartData) {
    window.GkifyEvents.emit(GkifyTheme.pubSubEvents.cartError, {
      source: 'product-form',
      productVariantId: productId,
      errors: cartData.description || '',
      message: cartData.message || 'Add to cart failed',
    });
  }

  async updateCartUI(cartData) {
    const { GkifySettings, GkifyStrings } = window;

    if (GkifySettings.use_ajax_atc) {
      if (this.cart && GkifySettings.enable_cart_drawer) {
        await this.updateCartDrawer(cartData, GkifyStrings);
      } else {
        this.showAddToCartNotification(GkifyStrings.itemAdded);
      }
    } else {
      window.location.href = GkifySettings.routes.cart;
    }

    window.GkifyEvents.emit(GkifyTheme.pubSubEvents.cartUpdate, cartData);
  }

  async updateCartDrawer(cartData, GkifyStrings) {
    this.cart.renderContents(cartData);
    this.showAddToCartNotification(GkifyStrings.itemAdded, true);
    window.GkifyEvents.emit(GkifyTheme.pubSubEvents.openCartDrawer);
  }

  showAddToCartNotification(message, isDrawer = false) {
    document.dispatchEvent(
      new CustomEvent('notification:show', {
        detail: {
          showOptions: {
            message,
            ...(isDrawer ? { delay: 400 } : { last: 3000 }),
          },
        },
      })
    );
  }

  handleError(error) {
    document.dispatchEvent(
      new CustomEvent('notification:show', {
        detail: {
          showOptions: {
            type: 'error',
            message: error.message || 'Add to cart failed',
            last: 3000,
          },
        },
      })
    );
  }

  handleProductChange(select) {
    const item = select.closest('.vip-points-products__item');
    const categoryData = this.productData.find(
      (data) => data.category === item.querySelector('.vip-points-products__item-title').textContent
    );

    if (categoryData) {
      this.updateProductInfo(item, categoryData);
    }
  }

  handleVariantChange(select) {
    const item = select.closest('.vip-points-products__item');
    const categoryData = this.productData.find(
      (data) => data.category === item.querySelector('.vip-points-products__item-title').textContent
    );

    if (categoryData) {
      this.updateProductInfo(item, categoryData);
    }
  }

  getSelectedProductId(button) {
    return button.dataset.productId || null;
  }

  getSelectedDiscountId(button) {
    return button.dataset.discountId || null;
  }

  renderProducts(products) {
    this.grid.innerHTML = '';
    this.productData = products;

    products.forEach((categoryData) => {
      const item = this.template.cloneNode(true);
      item.classList.remove('g:hidden');

      // 更新商品分类标题
      item.querySelector('.vip-points-products__item-title').textContent = categoryData.category;

      // 处理所有可能的选项
      const optionsContainer = item.querySelector('.vip-points-products__item-options');
      optionsContainer.innerHTML = ''; // 清空默认选项

      // 渲染设备选项
      const deviceSelect = item.querySelector('[data-product-select]');
      if (deviceSelect && categoryData.options.device?.length) {
        deviceSelect.innerHTML = categoryData.options.device
          .map((device) => `<option value="${device}">${device}</option>`)
          .join('');
      } else {
        deviceSelect?.closest('.vip-points-products__item-variants')?.remove();
      }

      // 渲染颜色选项
      if (categoryData.options.color?.length) {
        const colorOption = this.createOptionSelect('Color', 'color', categoryData.options.color);
        optionsContainer.appendChild(colorOption);
      }

      // 渲染样式选项
      if (categoryData.options.style?.length) {
        const styleOption = this.createOptionSelect('Style', 'style', categoryData.options.style);
        optionsContainer.appendChild(styleOption);
      }

      // 如果没有任何选项，隐藏选项容器
      if (!optionsContainer.children.length) {
        optionsContainer.remove();
      }

      // 更新按钮状态
      const button = item.querySelector('.vip-points-products__item-button');
      if (!this.customer_has_login) {
        button.classList.add('g-button--disabled');
      } else {
        button.classList.add('g-button--disabled');
      }

      // 初始化商品信息
      this.updateProductInfo(item, categoryData);

      this.grid.appendChild(item);
    });
  }

  // 创建选项选择器的辅助方法
  createOptionSelect(label, type, options) {
    const div = document.createElement('div');
    div.className = 'vip-points-products__item-option';

    const labelElement = document.createElement('label');
    labelElement.textContent = label;

    const select = document.createElement('select');
    select.className = 'vip-points-products__item-select';
    select.setAttribute(`data-${type}-select`, '');

    select.innerHTML = options.map((option) => `<option value="${option}">${option}</option>`).join('');

    div.appendChild(labelElement);
    div.appendChild(select);

    return div;
  }

  findMatchingProduct(products, device, color, style) {
    return products.find((product) => {
      const matches = [];

      // 检查设备匹配
      if (device) {
        const deviceMatch = product.metafields.find((meta) => meta.key === 'device' && meta.value === device);
        matches.push(!!deviceMatch);
      }

      // 只在有颜色选项时检查颜色匹配
      if (color) {
        const colorMatch = product.metafields.find((meta) => meta.key === 'color' && meta.value === color);
        matches.push(!!colorMatch);
      }

      // 只在有样式选项时检查样式匹配
      if (style) {
        const styleMatch = product.metafields.find((meta) => meta.key === 'style' && meta.value === style);
        matches.push(!!styleMatch);
      }

      // 所有存在的选项都必须匹配
      return matches.every(Boolean);
    });
  }

  updateProductInfo(item, categoryData) {
    const deviceSelect = item.querySelector('[data-product-select]');
    const colorSelect = item.querySelector('[data-color-select]');
    const styleSelect = item.querySelector('[data-style-select]');
    const button = item.querySelector('.vip-points-products__item-button');

    const selectedProduct = this.findMatchingProduct(
      categoryData.products,
      deviceSelect?.value,
      colorSelect?.value,
      styleSelect?.value
    );

    if (selectedProduct) {
      // 更新商品信息
      item.querySelector('.vip-points-products__item-name').textContent = selectedProduct.title;
      item.querySelector('.vip-points-products__item-points-value').textContent = selectedProduct.point_value;

      // 更新图片
      const imageContainer = item.querySelector('.vip-points-products__item-image');
      if (imageContainer) {
        if (selectedProduct.image) {
          // 创建新的图片元素
          const img = document.createElement('img');
          img.src = selectedProduct.image;
          img.alt = selectedProduct.title;
          img.width = 400;
          img.height = 400;
          img.classList.add('g:w-full', 'g:h-full');

          // 清除原有内容并添加新图片
          imageContainer.innerHTML = '';
          imageContainer.appendChild(img);
        } else {
          // 如果没有商品图片，恢复到默认占位图
          imageContainer.innerHTML = this.template.querySelector('.vip-points-products__item-image').innerHTML;
        }
      }

      // 更新按钮
      if (this.customer_has_login) {
        button.classList.remove('g-button--disabled');
        button.dataset.productId = selectedProduct.id;
        button.dataset.discountId = selectedProduct.discount_id;
      }
    } else {
      button.classList.add('g-button--disabled');
      button.dataset.productId = '';
      button.dataset.discountId = '';

      // 当没有找到匹配商品时，也恢复到默认占位图
      const imageContainer = item.querySelector('.vip-points-products__item-image');
      if (imageContainer) {
        imageContainer.innerHTML = this.template.querySelector('.vip-points-products__item-image').innerHTML;
      }
    }
  }

  showLoading() {
    this.loading.classList.remove('g:hidden');
    this.container.classList.add('g:hidden');
    this.empty.classList.add('g:hidden');
  }

  hideLoading() {
    this.loading.classList.add('g:hidden');
  }

  showEmpty() {
    this.empty.classList.remove('g:hidden');
    this.container.classList.add('g:hidden');
  }

  showContent() {
    this.empty.classList.add('g:hidden');
    this.container.classList.remove('g:hidden');
  }

  // 辅助方法：根据 productId 找到对应的商品对象
  findCurrentProduct(productId) {
    if (!this.productData) return null;

    for (const category of this.productData) {
      const product = category.products.find((p) => p.id.toString() === productId.toString());
      if (product) return product;
    }
    return null;
  }
}

customElements.define('vip-points-products-list', VIPPointsProductsList);
